import { Button, Text } from "@mantine/core";
import { Lock, Wallet } from "iconsax-react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { IProductComplete } from "../../interfaces/Products/IProductDetail";
import { ERole } from "../../interfaces/User/newUser";
import { trackEvent } from "../../utils/AnalyticsGoogle/AnalyticsInit";
import {
  postAddInterest,
  postBuyPlan,
  useMyPlans,
  useMyProducts,
} from "../../hooks";
import { useMutation } from "@tanstack/react-query";
import { EPlanStatus } from "../../interfaces/MyPlans";
import { useTranslation } from "react-i18next";
import { Modal, ModalLoading } from "../../components";
import { client_selected } from "../../client";
import { EServiceType } from "../../interfaces/MyPlans/IMyPlans";

interface IProps {
  selectedProduct: IProductComplete | undefined;
}

export default function ButtonCallToAction({ selectedProduct }: IProps) {
  const { t } = useTranslation();
  const { slug } = useParams();
  const id = slug?.split("&id=")[1];
  const navigate = useNavigate();
  const location = useLocation();
  const isGenial = client_selected.name === "Genial";
  const { setInterest, isAuthenticated, roleSelected } = useAuth();
  const roleSelectedIsAnalyst = roleSelected === ERole.analyst;
  const roleSelectedIsClient = roleSelected === ERole.client;
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isOpenModalError, setIsOpenModalError] = useState<string>("");
  const { data: dataMyProducts, isFetched: isFetchedMyProducts } =
    useMyProducts({
      enabled: roleSelectedIsAnalyst,
      active: EServiceType.All,
    });

  const { data: dataMyPlans, isFetched: isFetchedMyPlans } = useMyPlans({
    status: "",
    enabled: roleSelectedIsClient,
  });

  const isFetched = roleSelectedIsClient
    ? isFetchedMyPlans
    : isFetchedMyProducts || !isAuthenticated;

  const isOwned = roleSelectedIsAnalyst
    ? dataMyProducts?.data.some((myProduct) => myProduct.id === Number(id))
    : dataMyPlans?.dates.some((date) =>
        date.user_plans.some(
          (plan) =>
            plan.product.id === Number(id) &&
            (plan.status === EPlanStatus.Approved ||
              plan.status === EPlanStatus.InProcessing ||
              plan.status === EPlanStatus.CancelRequested)
        )
      );

  const product = !!selectedProduct
    ? selectedProduct
    : ({} as IProductComplete);

  const { mutate } = useMutation(postAddInterest, {
    onSuccess: (data) => {
      // console.log(data);
    },
    onError: (error) => {
      // console.log(error);
    },
  });
  const { mutate: mutateBuyPlan } = useMutation(postBuyPlan, {
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: (errors: any) => {
      console.error(t("marketplace.comprar_serviços.error_buy"), errors);
      setIsOpenModalError(errors.response.data.message.toString() || "");
    },
  });

  const handleAssign = () => {
    // Rastrear evento de compra
    trackEvent(
      "Produto",
      "Iniciar Compra",
      `Produto: ${product?.product?.title} - ID: ${product?.product?.id}`
    );
    const from = location;
    if (isAuthenticated) {
      setIsLoadingModal(true);

      mutate({ product_id: product?.product?.id });
      mutateBuyPlan({
        payment_info: null,
        payment_owner: null,
        plans: [
          {
            hasValue: !!product?.plan[0].price,
            plan_id: product?.plan[0].id,
            product_id: product?.product.id,
          },
        ],
      });
      setIsLoadingModal(false);
    } else {
      mutate({ product_id: product?.product?.id });
      isGenial
        ? navigate("/autenticacao/acessar-conta", {
            state: { from: from },
          })
        : navigate("/autenticacao/criar-conta", {
            state: { from: from },
          });
    }
  };
  const handleBuyProduct = () => {
    // Rastrear evento de compra
    trackEvent(
      "Produto",
      "Iniciar Compra",
      `Produto: ${product?.product?.title} - ID: ${product?.product?.id}`
    );

    setInterest((prevInterest) => {
      const productExists = prevInterest.some(
        (item) => item?.product?.id === product?.product?.id
      );

      if (productExists) {
        return prevInterest.map((item) =>
          item.product.id === product?.product?.id
            ? { ...item, selected: true }
            : { ...item, selected: false }
        );
      }

      return [
        ...prevInterest.map((item) => ({ ...item, selected: false })),
        { ...product, selected: true },
      ];
    });

    const buyProducts = "/comprar-servicos";
    const from = location.pathname;

    if (isAuthenticated) {
      navigate(buyProducts, { state: { from } });
    } else {
      mutate({ product_id: product?.product?.id });
      navigate("/autenticacao/criar-conta", {
        state: { from: buyProducts },
      });
    }
  };
  const handleSucessConfirmationClose = () => {
    setIsLoadingModal(false);
    setIsSuccess(false);
  };
  const handleRedirect = () => {
    navigate("/meu-portfolio/minhas-compras");
  };
  const handleCloseErrorModal = () => {
    setIsOpenModalError("");
  };
  return (
    <>
      {isLoadingModal ? (
        <ModalLoading open={isLoadingModal} />
      ) : (
        <Modal
          type={"success"}
          titleFirstButton={t(
            "marketplace.comprar_serviços.modal_success.title_first_button"
          )}
          titleSeccondButton={t("global.button.close")}
          description={t(
            "marketplace.comprar_serviços.modal_success.description"
          )}
          open={isSuccess}
          setClose={handleSucessConfirmationClose}
          secondClick={handleSucessConfirmationClose}
          firstClick={handleRedirect}
        />
      )}

      <Modal
        type={"danger"}
        titleFirstButton={t("global.button.close")}
        description={isOpenModalError}
        contentEspecial={
          <Text c="basic.4" fz="16px" lh="155%" ta={"center"} mb="32px">
            {t("marketplace.comprar_serviços.modal_danger.content_especial")}
          </Text>
        }
        textModal={t("marketplace.comprar_serviços.modal_danger.text_modal")}
        open={!!isOpenModalError}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
      />
      {roleSelectedIsAnalyst && !isOwned ? (
        <Text color="basic.5" fw={500} size={16}>
          {t("marketplace.detalhe.only_clients")}
        </Text>
      ) : isOwned ? null : (
        <Button
          variant={"primaryOutline"}
          leftIcon={
            !!isAuthenticated ? <Wallet size={24} /> : <Lock size={24} />
          }
          w="100%"
          onClick={isGenial ? handleAssign : handleBuyProduct}
          loading={!isFetched}
        >
          {isGenial
            ? t("marketplace.detalhe.buy_area.button_call_to_action_genial", {
                category: selectedProduct?.product.category,
              })
            : t("marketplace.detalhe.buy_area.button_call_to_action", {
                category: selectedProduct?.product.category,
              })}
        </Button>
      )}
    </>
  );
}
